import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Snowflake, Target, Building2, Clock, Lock, GripVertical, TrendingUp } from "lucide-react";
import api, { formatApiError, money } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const STAGES = ["Nuevo", "Contactado", "Calificado", "Cotización", "Negociación"];
// Columnas del embudo, en orden
const COLUMNS = [
  { key: "Frío", kind: "cold", accent: "border-t-sky-400", head: "text-sky-600", icon: Snowflake },
  { key: "Nuevo", kind: "stage", accent: "border-t-blue-400", head: "text-blue-600", icon: Target },
  { key: "Contactado", kind: "stage", accent: "border-t-cyan-400", head: "text-cyan-600", icon: Target },
  { key: "Calificado", kind: "stage", accent: "border-t-indigo-400", head: "text-indigo-600", icon: Target },
  { key: "Cotización", kind: "stage", accent: "border-t-amber-400", head: "text-amber-600", icon: Target },
  { key: "Negociación", kind: "stage", accent: "border-t-orange-400", head: "text-orange-600", icon: Target },
  { key: "Cliente", kind: "client", accent: "border-t-emerald-500", head: "text-emerald-600", icon: Building2 },
  { key: "Perdido", kind: "stage", accent: "border-t-rose-400", head: "text-rose-600", icon: Target },
];

function daysLeft(iso) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return d;
}

function LockPill({ item }) {
  if (item.lock_status === "expired") return <Badge className="rounded-sm text-[9px] gap-1 bg-destructive/15 text-destructive"><Clock className="h-2.5 w-2.5" /> Vencido</Badge>;
  if (item.lock_status === "active") {
    const d = daysLeft(item.locked_until);
    if (d != null && d <= 7) return <Badge className="rounded-sm text-[9px] gap-1 bg-amber-500/15 text-amber-600"><Clock className="h-2.5 w-2.5" /> {d}d</Badge>;
    return <Badge className="rounded-sm text-[9px] gap-1 bg-emerald-500/15 text-emerald-600"><Lock className="h-2.5 w-2.5" /> Activo</Badge>;
  }
  return null;
}

export default function Funnel() {
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/prospects"), api.get("/clients")])
      .then(([p, c]) => { setProspects(p.data); setClients(c.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const buckets = useMemo(() => {
    const b = {}; COLUMNS.forEach((c) => (b[c.key] = []));
    prospects.forEach((p) => {
      if (p.segment === "cold") { b["Frío"].push({ ...p, _type: "prospect" }); return; }
      if (p.converted_client_id || p.stage === "Ganado") return; // ya es cliente
      const st = STAGES.includes(p.stage) ? p.stage : (p.stage === "Perdido" ? "Perdido" : "Nuevo");
      b[st].push({ ...p, _type: "prospect" });
    });
    clients.forEach((c) => b["Cliente"].push({ ...c, _type: "client", company: c.business_name, estimated_value: 0 }));
    return b;
  }, [prospects, clients]);

  const totalValue = (col) => buckets[col].reduce((a, x) => a + Number(x.estimated_value || 0), 0);

  const onDrop = async (col) => {
    setOverCol(null);
    const id = dragId; setDragId(null);
    if (!id) return;
    const p = prospects.find((x) => x.id === id);
    if (!p || p.segment === "cold" || col.kind !== "stage") return;
    if (p.stage === col.key) return;
    setProspects((prev) => prev.map((x) => (x.id === id ? { ...x, stage: col.key } : x))); // optimista
    try { await api.put(`/prospects/${id}/stage`, { stage: col.key }); toast.success(`Movido a ${col.key}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const totalProspects = prospects.filter((p) => p.segment !== "cold" && !p.converted_client_id && p.stage !== "Ganado").length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Embudo comercial</h1>
          <p className="text-sm text-muted-foreground">Todos tus clientes y prospectos por etapa. Arrastra una tarjeta de prospecto para cambiar su paso.</p>
        </div>
        <div className="flex gap-4 text-xs">
          <div className="text-right"><p className="text-muted-foreground uppercase tracking-widest text-[10px]">Prospectos</p><p className="text-lg font-heading font-bold">{totalProspects}</p></div>
          <div className="text-right"><p className="text-muted-foreground uppercase tracking-widest text-[10px]">Clientes</p><p className="text-lg font-heading font-bold text-emerald-600">{buckets["Cliente"].length}</p></div>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="funnel-board">
          {COLUMNS.map((col) => {
            const items = buckets[col.key];
            const isDropOk = col.kind === "stage";
            return (
              <div key={col.key}
                onDragOver={(e) => { if (isDropOk) { e.preventDefault(); setOverCol(col.key); } }}
                onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
                onDrop={() => onDrop(col)}
                className={`flex-shrink-0 w-[220px] rounded-sm border border-t-2 bg-card ${col.accent} ${overCol === col.key ? "ring-2 ring-primary/50 bg-primary/5" : ""} transition-colors`}
                data-testid={`funnel-col-${col.key}`}>
                <div className="px-3 py-2.5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
                  <div className="flex items-center gap-1.5">
                    <col.icon className={`h-3.5 w-3.5 ${col.head}`} />
                    <span className="text-xs font-bold uppercase tracking-wider">{col.key}</span>
                  </div>
                  <Badge className="rounded-sm bg-muted text-foreground text-[10px]">{items.length}</Badge>
                </div>
                {col.key !== "Cliente" && col.key !== "Frío" && totalValue(col.key) > 0 && (
                  <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-b border-border flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {money("GTQ", totalValue(col.key))}</div>
                )}
                <div className="p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto">
                  {items.length === 0 && <p className="text-[11px] text-muted-foreground text-center py-4">—</p>}
                  {items.map((it) => {
                    const draggable = it._type === "prospect" && it.segment !== "cold";
                    return (
                      <div key={it.id} draggable={draggable}
                        onDragStart={() => draggable && setDragId(it.id)}
                        onDragEnd={() => setDragId(null)}
                        className={`rounded-sm border border-border bg-background p-2.5 ${draggable ? "cursor-grab active:cursor-grabbing hover:border-primary/50" : ""} ${dragId === it.id ? "opacity-40" : ""} transition-all`}
                        data-testid={`funnel-card-${it.id}`}>
                        <div className="flex items-start gap-1.5">
                          {draggable && <GripVertical className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold leading-tight truncate">{it.company}</p>
                            {it._type === "client"
                              ? <p className="text-[10px] text-muted-foreground truncate">{it.vendor_name || "—"}</p>
                              : <p className="text-[10px] text-muted-foreground">{it.contact || it.industry || "—"}</p>}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-1.5 gap-1">
                          {it._type === "prospect" && Number(it.estimated_value) > 0
                            ? <span className="text-[10px] font-mono font-semibold">{money("GTQ", it.estimated_value)}</span>
                            : <span />}
                          <LockPill item={it} />
                        </div>
                        {it.segment === "cold" && <p className="text-[9px] text-sky-600 mt-1">Pool compartido</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
